'use client';

import type { ReactNode } from 'react';
import { styled, css } from 'styled-components';
import cs from 'classnames';

import Link from '@dvd/components/resource-link';
import {
  imageZoomOnHoverStyles,
  imageZoomHoverActiveStyles,
} from '@lib/styles';
import { TextLinkStyles, TextLinkHoverStyles } from '@dvd/components/TextLink';
import { trackButton } from '@lib/tracking';
import type { CardColor, CtaProps, SafeUrl } from '@types';
import ContentfulImage from '@dvd/components/ContentfulImage';

type ImageCardProps = {
  /**
   * The image for the card
   */
  imgSrc: SafeUrl;
  /**
   * Alt text for the image
   */
  alt: string;
  /**
   * Heading for the card
   */
  heading: string;
  /**
   * Body text for the card
   * Can accept either a string, or a Fragment with inline-level elements only
   */
  body: ReactNode;
  /**
   * The color scheme of the card
   */
  colorScheme?: Exclude<CardColor, 'darkBlue'>; // Grabbing all card colors except darkBlue, which this component doesn't use!
  /**
   * Optional cta for the card
   */
  cta?: CtaProps;
};

const ImageCardParagraph = styled.p`
  margin: 0;

  && a {
    color: inherit;
  }
`;

const ImageCardImageWrapper = styled.div`
  ${imageZoomOnHoverStyles}
  border-top-left-radius: ${({ theme }) => theme.radius.std};
  border-top-right-radius: ${({ theme }) => theme.radius.std};
  width: 100%;

  /* At medium the card is a horizontal grid (image left, body right),
     so the image's exposed corners are top-left and bottom-left. */
  ${({ theme }) => theme.mq.minWidth.medium} {
    border-top-right-radius: 0;
    border-bottom-left-radius: ${({ theme }) => theme.radius.std};
  }

  /* At large the card returns to a column layout (image above body). */
  ${({ theme }) => theme.mq.minWidth.large} {
    border-top-right-radius: ${({ theme }) => theme.radius.std};
    border-bottom-left-radius: 0;
  }
`;

const ImageCardImage = styled(ContentfulImage)`
  border-radius: 0;
  display: block;
  height: auto;
  width: 100%;
`;

const ImageCardBody = styled.div`
  border-bottom-left-radius: ${({ theme }) => theme.radius.std};
  border-bottom-right-radius: ${({ theme }) => theme.radius.std};
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[2]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    align-self: center;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    align-self: unset;
    /* When ImageCardColumns stretches a row of cards to equal height, the
       body absorbs the extra space so the CTA can sit along the bottom. */
    flex-grow: 1;
  }
`;

const ImageCardHeading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing[0.75]};
`;

const ImageCardButton = styled.div`
  ${TextLinkStyles}
  color: inherit;
  /* Auto top margin pins the link to the bottom of a stretched body so CTAs
     line up across a row of ImageCardColumns cards; the gap above it comes
     from ImageCardParagraph's bottom margin in the CTA variant. */
  margin: auto 0 0;
  width: fit-content;
`;

const sharedImageCardWrapperStyles = css`
  border-radius: ${({ theme }) => theme.radius.std};
  display: flex;
  flex-direction: column;

  &.blue {
    background-color: ${({ theme }) => theme.colors.blue200};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.white {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.green300};
    color: ${({ theme }) => theme.colors.green800};
  }

  &.pink {
    background-color: ${({ theme }) => theme.colors.pink300};
    color: ${({ theme }) => theme.colors.pink800};
  }

  &.orange {
    background-color: ${({ theme }) => theme.colors.orange200};
    color: ${({ theme }) => theme.colors.orange800};
  }

  &.purple {
    background-color: ${({ theme }) => theme.colors.purple300};
    color: ${({ theme }) => theme.colors.purple800};
  }

  &.yellow {
    background-color: ${({ theme }) => theme.colors.yellow200};
    color: ${({ theme }) => theme.colors.yellow800};
  }

  ${ImageCardParagraph} {
    font-size: ${({ theme }) => theme.typography.fontSize.bodyMd};
    font-weight: ${({ theme }) => theme.typography.fontWeight.body};
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    align-items: center;
    ${({ theme }) => theme.grid.columns.medium};
    column-gap: ${({ theme }) => theme.spacing[2]};
    display: grid;

    > * {
      grid-column: auto / span 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    ${ImageCardParagraph} {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodyMd};
    }
  }
`;

const ImageCardLink = styled(Link)`
  ${sharedImageCardWrapperStyles}

  ${ImageCardParagraph} {
    margin-block-end: ${({ theme }) => theme.spacing[1.5]};
  }

  &:hover,
  &:focus-visible {
    ${imageZoomHoverActiveStyles}
  }

  &.white {
    &:visited {
      color: ${({ theme }) => theme.colors.blue800};
    }
    &:hover,
    &:focus-visible {
      box-shadow: ${({ theme }) =>
        theme.elevation.elevation1(theme.colors.blue800)};
      color: ${({ theme }) => theme.colors.blue800};
    }
  }

  &:hover.blue,
  &:focus-visible.blue {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.blue800)};
    color: ${({ theme }) => theme.colors.blue800};
  }
  &:visited.blue {
    color: ${({ theme }) => theme.colors.blue800};
  }

  &:hover.green,
  &:focus-visible.green {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.green800)};
    color: ${({ theme }) => theme.colors.green800};
  }
  &:visited.green {
    color: ${({ theme }) => theme.colors.green800};
  }

  &:hover.pink,
  &:focus-visible.pink {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.pink800)};
    color: ${({ theme }) => theme.colors.pink800};
  }
  &:visited.pink {
    color: ${({ theme }) => theme.colors.pink800};
  }

  &:hover.orange,
  &:focus-visible.orange {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.orange800)};
    color: ${({ theme }) => theme.colors.orange800};
  }
  &:visited.orange {
    color: ${({ theme }) => theme.colors.orange800};
  }

  &:hover.purple,
  &:focus-visible.purple {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.purple800)};
    color: ${({ theme }) => theme.colors.purple800};
  }
  &:visited.purple {
    color: ${({ theme }) => theme.colors.purple800};
  }

  &:hover.yellow,
  &:focus-visible.yellow {
    box-shadow: ${({ theme }) =>
      theme.elevation.elevation1(theme.colors.yellow800)};
    color: ${({ theme }) => theme.colors.yellow800};
  }
  &:visited.yellow {
    color: ${({ theme }) => theme.colors.yellow800};
  }

  &:hover ${ImageCardButton}, &:focus-visible ${ImageCardButton} {
    ${TextLinkHoverStyles}
    color: inherit;
  }
`;

const ImageCardWrapper = styled.div`
  ${sharedImageCardWrapperStyles}
`;

const ImageCard = ({
  alt,
  body,
  colorScheme = 'blue',
  heading,
  imgSrc,
  cta = null,
}: ImageCardProps) => {
  const handleClick = () => {
    if (cta.trackingLabel && cta.trackingProperty) {
      trackButton(cta.trackingLabel, cta.trackingProperty);
    }
  };

  return cta ? (
    <ImageCardLink
      className={cs(colorScheme)}
      href={cta.href}
      onClick={handleClick}
    >
      <ImageCardImageWrapper>
        <ImageCardImage alt={alt} height={320} src={imgSrc} width={416} />
      </ImageCardImageWrapper>
      <ImageCardBody>
        <ImageCardHeading>{heading}</ImageCardHeading>
        <ImageCardParagraph>{body}</ImageCardParagraph>
        <ImageCardButton>{cta.text}</ImageCardButton>
      </ImageCardBody>
    </ImageCardLink>
  ) : (
    <ImageCardWrapper className={cs(colorScheme)}>
      <ImageCardImageWrapper>
        <ImageCardImage alt={alt} height={320} src={imgSrc} width={416} />
      </ImageCardImageWrapper>
      <ImageCardBody>
        <ImageCardHeading>{heading}</ImageCardHeading>
        <ImageCardParagraph>{body}</ImageCardParagraph>
      </ImageCardBody>
    </ImageCardWrapper>
  );
};

export default ImageCard;
